/* eslint-disable camelcase */
import { useCallback, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { createLocale } from 'locales';

import { showErrorToast, showSuccessToast } from 'components-new/toast/toast';

import type {
  ApiOrganisationSdbListParams,
  OrganisationAddSDBCredentials,
  SDBJobs,
} from 'state/__generated__/tadaahAPI';
import { useApiOrganisationSdbList } from 'state/__generated__/tadaahAPI';
import { useOrganisationContext } from 'state/context/OrganisationContext';
import { useIntegrationSettingsContext } from 'state/context/organization/OrganisationSettings/IntegrationSettingsContext';

import organisationSettingsRoutes, {
  IntegrationSettingsBaseRoute,
} from 'pages/organization/OrganisationSettingsPage/integration-settings/organisation-settings-routes';

import useSdbIntegration from './use-sdb-integration';

const globalLocalize = createLocale('global');
const localize = createLocale('organisationIntegrationSettingsLocales');
export interface IntegrationProvider {
  description: string;
  hasValidCredentials: boolean;
  id: number;
  image: string;
  name: string;
  page: string;
}

export interface UseOrganisationSettingsIntegrationHandler {
  apiKeyForm: {
    apiKeyFieldType: 'text' | 'password';
    initialValues: { sdb_api_key: string; sdb_account_name: string };
    onFormSubmit: (values: OrganisationAddSDBCredentials) => void;
    isLoading: boolean;
    toggleApiKeyFieldType: () => void;
  };
  fetchJobsForm: {
    initialValues: ApiOrganisationSdbListParams;
    onFormSubmit: () => void;
  };
  integrations: {
    providers: IntegrationProvider[];
    isLoading: boolean;
  };
  sdbJobs: {
    data: SDBJobs[] | undefined;
    error: string | undefined;
    isFetched: boolean;
    isLoading: boolean;
    refetch: (options: {
      throwOnError?: boolean;
      cancelRefetch?: boolean;
    }) => unknown;
  };
}

interface UseOrganisationSettingsIntegrationParams {
  fetchSdbJobsParams?: ApiOrganisationSdbListParams;
}

export const useOrganisationSettingsIntegration = ({
  fetchSdbJobsParams,
}: UseOrganisationSettingsIntegrationParams): UseOrganisationSettingsIntegrationHandler => {
  const navigate = useNavigate();
  const {
    organisations: {
      organisations,
      selectedOrganisationId,
      isLoading: organisationDataIsLoading,
    },
  } = useOrganisationContext();

  const {
    fetch: { setFetchedJobs, hasFetchedSdbJobs, setHasFetchedSdbJobs },
    settings: {
      apiKeyFieldType,
      hasClickedUpdateApiKey,
      setApiKeyFieldType,
      setHasClickedUpdateApiKey,
    },
  } = useIntegrationSettingsContext();

  const hasValidCredentials = !!organisations?.find(
    (organisation) => organisation.id === selectedOrganisationId,
  )?.sdb_status;

  const integrationProviders: IntegrationProvider[] = [
    {
      description: localize('sdbIntegrationDescription'),
      hasValidCredentials,
      id: 1,
      image: '/sdb.svg',
      name: 'SDB Planning',
      page: 'SDBIntegrationPage',
    },
  ];

  const {
    data: fetchedSdbData,
    isLoading: fetchedSdbIsLoading,
    error: fetchedSdbError,
    isSuccess: fetchedSdbIsSuccess,
    isFetched: fetchedSdbIsFetched,
    isFetching: fetchedSdbIsFetching,
    refetch: fetchedSdbRefetch,
  } = useApiOrganisationSdbList(
    selectedOrganisationId?.toString() || '',
    fetchSdbJobsParams as ApiOrganisationSdbListParams,
    {
      query: {
        enabled:
          !!selectedOrganisationId && hasFetchedSdbJobs && !!fetchSdbJobsParams,
        queryKey: ['fetch-sdb-jobs', selectedOrganisationId],
        refetchOnWindowFocus: false,
        retry: 2,
      },
    },
  );

  const {
    update: {
      updateSDBCredentials,
      error: updateSDBError,
      isLoading: updateSDBIsLoading,
      isSuccess: updateSDBIsSuccess,
    },
    get: { sdbApiKey, sdbAccountName },
  } = useSdbIntegration({ organisationId: selectedOrganisationId?.toString() });

  useEffect(() => {
    // after the fetch-sdb-jobs query is called, we check for the status and determine the next action
    if (!fetchedSdbIsFetching && fetchedSdbIsFetched && hasFetchedSdbJobs) {
      if (!fetchedSdbIsSuccess && fetchedSdbError) {
        const errorMessage = fetchedSdbError?.response?.data
          ? JSON.stringify(fetchedSdbError?.response.data)
          : fetchedSdbError?.message;
        showErrorToast({
          description: errorMessage,
          title: globalLocalize('error'),
        });
      }
      if (fetchedSdbIsSuccess && fetchedSdbData) {
        if (!fetchedSdbData.data.length) {
          showErrorToast({
            description: localize('noJobsFoundInDateRange'),
            title: localize('noJobsFound'),
          });
        } else {
          setFetchedJobs(fetchedSdbData.data);

          showSuccessToast({
            description: `${fetchedSdbData.data.length} ${localize(
              'jobsFetchedFromSDB',
            )}`,
            title: localize('jobsFound'),
          });

          navigate(
            IntegrationSettingsBaseRoute +
              organisationSettingsRoutes.selectJobs.path,
          );
        }
      }
      setHasFetchedSdbJobs(false);
    }
  }, [
    fetchedSdbData,
    fetchedSdbError,
    hasFetchedSdbJobs,
    fetchedSdbIsFetched,
    fetchedSdbIsSuccess,
    navigate,
    setFetchedJobs,
    fetchedSdbIsFetching,
    setHasFetchedSdbJobs,
  ]);

  useEffect(() => {
    if (hasClickedUpdateApiKey) {
      if (updateSDBError) {
        showErrorToast({ description: updateSDBError, title: 'Error' });
      }
      if (updateSDBIsSuccess) {
        showSuccessToast({
          description: localize('authenticationSuccessfulMessage'),
          title: localize('authenticationSuccessful'),
          withIcon: true,
        });
        navigate(
          IntegrationSettingsBaseRoute +
            organisationSettingsRoutes.sdbIntegration.path,
        );
      }
      setHasClickedUpdateApiKey(false);
    }
  }, [
    hasClickedUpdateApiKey,
    navigate,
    setHasClickedUpdateApiKey,
    updateSDBError,
    updateSDBIsSuccess,
  ]);

  const apiKeyFormInitialValues = {
    sdb_account_name: sdbAccountName || '',
    sdb_api_key: sdbApiKey || '',
  };
  const fetchJobsFormInitialValues = {
    end_date: '',
    start_date: '',
  };

  const toggleApiKeyFieldType = useCallback(() => {
    setApiKeyFieldType((prev) => {
      if (prev === 'password') return 'text';
      return 'password';
    });
  }, [setApiKeyFieldType]);

  const onApiKeyFormSubmit = useCallback(
    async (values: OrganisationAddSDBCredentials) => {
      try {
        await updateSDBCredentials(values);
      } finally {
        setHasClickedUpdateApiKey(true);
      }
    },
    [setHasClickedUpdateApiKey, updateSDBCredentials],
  );

  const onFetchJobsFormSubmit = useCallback(async () => {
    if (!fetchedSdbIsFetched) {
      setHasFetchedSdbJobs(true);
    } else {
      await fetchedSdbRefetch();
      setHasFetchedSdbJobs(true);
    }
  }, [fetchedSdbIsFetched, fetchedSdbRefetch, setHasFetchedSdbJobs]);

  return {
    apiKeyForm: {
      apiKeyFieldType,
      initialValues: apiKeyFormInitialValues,
      isLoading: updateSDBIsLoading,
      onFormSubmit: onApiKeyFormSubmit,
      toggleApiKeyFieldType,
    },
    fetchJobsForm: {
      initialValues: fetchJobsFormInitialValues,
      onFormSubmit: onFetchJobsFormSubmit,
    },
    integrations: {
      isLoading: organisationDataIsLoading,
      providers: integrationProviders,
    },
    sdbJobs: {
      data: fetchedSdbData?.data,
      error: fetchedSdbError?.message,
      isFetched: fetchedSdbIsFetched,
      isLoading: fetchedSdbIsLoading,
      refetch: fetchedSdbRefetch,
    },
  };
};
